'use client';

import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import styles from './CashbackButton.module.css';
import { AppContext } from '../../context/AppContext';

const CashbackModal = dynamic(() => import('./CashbackModal'), {
  loading: () => null,
});

const CASHBACK_BALANCE_THRESHOLD = 1;

const CASHBACK_BALANCE_MESSAGE =
  'Cashback can be claimed when your balance is below $1.';

const TOAST_DURATION_MS = 8000;

function getDisplayBalanceAmount(user) {
  const balance = Number(user?.balance);
  if (Number.isFinite(balance)) {
    return balance;
  }

  const bonus = Number(user?.bonusBalance);
  const withdrawable = Number(user?.withdrawableBalance);

  if (Number.isFinite(bonus) || Number.isFinite(withdrawable)) {
    return (
      (Number.isFinite(bonus) ? bonus : 0) +
      (Number.isFinite(withdrawable) ? withdrawable : 0)
    );
  }

  return null;
}

/**
 * Header cashback action.
 *
 * Rendering requires a logged-in API user; the cashback service is keyed on the
 * username from get-user-info. The quote is requested exclusively when the modal opens.
 */
export default function CashbackButton() {
  const { user, refreshBalanceFromCDK } = useContext(AppContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const username =
    typeof user?.username === 'string' ? user.username.trim() : '';
  const sessionReady = Boolean(username);
  const balanceAmount = getDisplayBalanceAmount(user);
  const isCashbackAvailable =
    balanceAmount !== null && balanceAmount < CASHBACK_BALANCE_THRESHOLD;

  useEffect(() => {
    if (!sessionReady) {
      setIsModalOpen(false);
    }
  }, [sessionReady]);

  useEffect(() => {
    if (!toastMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setToastMessage(null);
    }, TOAST_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [toastMessage]);

  const handleClose = useCallback(() => setIsModalOpen(false), []);

  const handleClaimSuccess = useCallback(() => {
    void refreshBalanceFromCDK?.();
  }, [refreshBalanceFromCDK]);

  const handleClick = useCallback(() => {
    if (!isCashbackAvailable) {
      setToastMessage(CASHBACK_BALANCE_MESSAGE);
      return;
    }

    setIsModalOpen(true);
  }, [isCashbackAvailable]);

  if (!sessionReady) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={`${styles.button} ${isCashbackAvailable ? styles.pulse : styles.inactive}`}
        onClick={handleClick}
        aria-label="Cashback"
        aria-disabled={!isCashbackAvailable}
        title="Cashback"
      >
        <Image
          src="/assets/cashback-image.png"
          alt="Cashback"
          width={100}
          height={100}
          className={styles.icon}
          priority
        />
      </button>

      {toastMessage ? (
        <div className={styles.toast} role="status">
          {toastMessage}
        </div>
      ) : null}

      {isModalOpen ? (
        <CashbackModal
          username={username}
          onClose={handleClose}
          onClaimSuccess={handleClaimSuccess}
        />
      ) : null}
    </>
  );
}
